import { Box } from '@mui/material';
import React, { useEffect } from 'react';

import { SUBMENU_BOX_SHADOW } from '../Components/styleConstants';

import { DATA_TEST } from '../enums';
import { IContextSwitcherData, INavBarOptions } from '../types';

import { ClickAwayAndKeydownListener } from '../Components/ClickAwayAndKeydownListener';
import { DesktopSwitchMenu } from '../Components/SwitchMenu/DesktopSwitchMenu';
import { useSwitchMenuContext } from '../Components/SwitchMenu/SwitchMenuContext';
import { DEFAULT_HEADER_ERROR_MESSAGE, delayedMobileCloseClickHandler } from '../Components/utils';
import { useMenuContext } from '../ContextState/useMenuContext';
import { FavoritesDisplayGrid } from '../Favorites/FavoritesDisplayGrid';
import { LevelOneMenu } from '../LevelOne/LevelOneMenu';
import { DesktopUtilityTray } from '../UtilityTray/DesktopUtilityTray';

import { ApplicationNameWithContextSwitcher } from './ApplicationNameWithContextSwitcher';

export interface IDesktopNavBarProps
  extends Pick<
    INavBarOptions,
    'appName' | 'customHeaderErrorMessage' | 'emptyFavoritesHeader' | 'emptyFavoritesMessage' | 'showOfflineIndicator'
  > {
  contextSwitcherData?: IContextSwitcherData;
  condensedUtilityTray?: boolean;
}

export const DesktopNavBar: React.FC<IDesktopNavBarProps> = ({
  appName,
  customHeaderErrorMessage = DEFAULT_HEADER_ERROR_MESSAGE,
  emptyFavoritesHeader,
  emptyFavoritesMessage,
  contextSwitcherData,
  showOfflineIndicator,
  condensedUtilityTray
}) => {
  const {
    showMainSubmenu,
    showFavoritesSubmenu,
    setShowFavoritesSubmenu,
    setShowMoreMenu,
    setShowMainSubmenu,
    currentLevelOneItem,
    showMoreMenu,
    disableFavorites,
    favorites,
    setCurrentLevelOneItem
  } = useMenuContext();
  const { menuButtonLabel: switchMenuButtonLabel, menuItems: switchMenuItems } = useSwitchMenuContext();
  const showSwitchMenu = switchMenuButtonLabel && switchMenuItems?.length > 0;

  // if the currentLevelOneItem is null and the main submenu is open,
  // close the main submenu
  // to ensure it is not open is the user toggles to mobile view
  useEffect(() => {
    if (!currentLevelOneItem && showMainSubmenu) {
      setShowMainSubmenu(false);
    }
  }, [currentLevelOneItem, showMainSubmenu]);

  // Setting the current level one item to null
  // will close all other open submenus and reset menu state
  // by triggering the hook below this one
  useEffect(() => {
    if (showFavoritesSubmenu) {
      setShowMoreMenu(false);
      setShowMainSubmenu(false);
      setCurrentLevelOneItem(null);
    }
  }, [showFavoritesSubmenu]);

  // If a new level one item is selected,
  // it means the main submenu should be visible.
  // In that case, the favorites and more menus should be closed
  useEffect(() => {
    if (currentLevelOneItem) {
      setShowFavoritesSubmenu(false);
      setShowMoreMenu(false);
      setShowMainSubmenu(true);
    }
  }, [currentLevelOneItem]);

  // If the more menu is open, close the main and favorites menus
  useEffect(() => {
    if (showMoreMenu) {
      setShowMainSubmenu(false);
      setShowFavoritesSubmenu(false);
      setCurrentLevelOneItem(null);
    }
  }, [showMoreMenu]);

  const closeMainSubmenu = () => {
    setCurrentLevelOneItem(null);
  };

  const navContainerRef: React.RefObject<HTMLDivElement> = React.useRef(null);
  return (
    <Box
      data-test={DATA_TEST.nav_bar_and_submenu_container}
      display="flex"
      flexGrow={1}
      flexShrink={0}
      width="100%"
      ref={navContainerRef}
    >
      <Box
        component="nav"
        aria-label="Main navigation"
        data-test={DATA_TEST.nav_bar_header}
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        flex={1}
        height={52}
        px={2}
      >
        <ApplicationNameWithContextSwitcher
          appName={appName}
          contextSwitcherData={contextSwitcherData}
          closeOpenSubmenu={() => {
            if (showMainSubmenu || showFavoritesSubmenu) closeMainSubmenu();
          }}
        />
        <LevelOneMenu
          navContainerRef={navContainerRef}
          customHeaderErrorMessage={customHeaderErrorMessage}
          showOfflineIndicator={showOfflineIndicator}
        />
        <Box sx={{ pl: 5, height: '100%' }} display="flex" flexShrink={0} data-test={DATA_TEST.nav_utilities_container}>
          {showSwitchMenu && <DesktopSwitchMenu />}
          <DesktopUtilityTray condensedIcons={false} collapseOptions={condensedUtilityTray} />
        </Box>
      </Box>
      {!disableFavorites && showFavoritesSubmenu && (
        <ClickAwayAndKeydownListener
          handleTrigger={() => {
            delayedMobileCloseClickHandler(() => {
              setShowFavoritesSubmenu(false);
            });
          }}
          triggerKeys={['Escape']}
        >
          <Box
            data-test={DATA_TEST.favorites_submenu_container}
            sx={{
              position: 'absolute',
              display: 'flex',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              flexDirection: 'column',
              padding: 2,
              width: '90%',
              maxWidth: favorites?.length >= 2 ? '1200px' : '400px',
              // Includes a 2px gap between nav bar and submenu containers
              marginTop: '55px',
              backgroundColor: 'background.paper',
              boxShadow: SUBMENU_BOX_SHADOW,
              maxHeight: '75vh',
              overflowX: 'auto',
              marginLeft: 6.5,
              zIndex: 3
            }}
          >
            <FavoritesDisplayGrid
              emptyFavoritesHeader={emptyFavoritesHeader}
              emptyFavoritesMessage={emptyFavoritesMessage}
            />
          </Box>
        </ClickAwayAndKeydownListener>
      )}
    </Box>
  );
};
